import React, { useRef, useEffect, useState, useCallback } from 'react';
import Hls from 'hls.js';
import {
  Card,
  Group,
  ActionIcon,
  Text,
  Badge,
  Stack,
  Alert,
  LoadingOverlay,
  Tooltip,
  Progress,
  Box,
  Button,
} from '@mantine/core';
import {
  IconPlayerPlay,
  IconPlayerPause,
  IconPlayerStop,
  IconVolume,
  IconVolumeOff,
  IconMaximize,
  IconMinimize,
  IconRefresh,
  IconAlertTriangle,
  IconWifi,
  IconWifiOff,
  IconSettings,
  IconRecordMail,
  IconCamera,
} from '@tabler/icons-react';

export interface RTSPVideoPlayerProps {
  rtspUrl: string;
  cameraName: string;
  cameraId: string;
  autoPlay?: boolean;
  showControls?: boolean;
  showStatus?: boolean;
  width?: string | number;
  height?: string | number;
  onError?: (error: string) => void;
  onStatusChange?: (status: 'loading' | 'playing' | 'paused' | 'error' | 'stopped') => void;
  className?: string;
}

export interface VideoStatus {
  isPlaying: boolean;
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
  quality: 'high' | 'medium' | 'low';
  bitrate?: number;
  fps?: number;
}

export const RTSPVideoPlayer: React.FC<RTSPVideoPlayerProps> = ({
  rtspUrl,
  cameraName,
  cameraId: _cameraId, // Prefix with underscore to indicate intentionally unused
  autoPlay = true,
  showControls = true,
  showStatus = true,
  width = '100%',
  height = 400,
  onError,
  onStatusChange,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [status, setStatus] = useState<VideoStatus>({
    isPlaying: false,
    isLoading: true,
    hasError: false,
    connectionStatus: 'connecting',
    quality: 'high',
  });
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // Convert RTSP URL to web-compatible format
  const getStreamUrl = (rtspUrl: string): string => {
    if (!rtspUrl || !rtspUrl.startsWith('rtsp://')) {
      return '';
    }

    // Extract stream path from RTSP URL
    // Example: rtsp://rtsp.yochienai.ca:8554/playground -> playground
    const urlParts = rtspUrl.split('/');
    const streamPath = urlParts[urlParts.length - 1];

    // Convert to HLS format through MediaMTX server
    // Production MediaMTX server is deployed at stream.yochienai.ca (routed via ingress)
    // Development and production use the same URL format
    const mediaServerBase = import.meta.env.VITE_MEDIA_SERVER_URL || 'https://stream.yochienai.ca';

    // Generate HLS stream URL: https://stream.yochienai.ca/playground/index.m3u8
    const hlsUrl = `${mediaServerBase}/${streamPath}/index.m3u8`;

    return hlsUrl;
  };

  const updateStatus = useCallback((newStatus: Partial<VideoStatus>) => {
    setStatus(prev => {
      const updated = { ...prev, ...newStatus };
      onStatusChange?.(
        updated.hasError ? 'error' :
        updated.isLoading ? 'loading' :
        updated.isPlaying ? 'playing' :
        'stopped'
      );
      return updated;
    });
  }, [onStatusChange]);

  const handlePlay = useCallback(async () => {
    if (!videoRef.current) return;

    try {
      updateStatus({ isLoading: true, hasError: false });
      await videoRef.current.play();
      updateStatus({
        isPlaying: true,
        isLoading: false,
        connectionStatus: 'connected'
      });
    } catch (error) {
      console.error('Error playing video:', error);
      // Call handleError directly to avoid circular dependency
      const errorMessage = 'Failed to play video stream';
      updateStatus({
        hasError: true,
        isLoading: false,
        isPlaying: false,
        errorMessage,
        connectionStatus: 'disconnected'
      });
      onError?.(errorMessage);
    }
  }, [updateStatus, onError]);

  const handlePause = () => {
    if (!videoRef.current) return;

    videoRef.current.pause();
    updateStatus({ isPlaying: false });
  };

  const handleStop = () => {
    if (!videoRef.current) return;

    videoRef.current.pause();
    videoRef.current.currentTime = 0;
    updateStatus({
      isPlaying: false,
      connectionStatus: 'disconnected'
    });
  };

  const handleError = useCallback((errorMessage: string) => {
    console.error('RTSP Video Error:', errorMessage);

    // Provide more helpful error messages for streaming
    let enhancedErrorMessage = errorMessage;
    const streamUrl = getStreamUrl(rtspUrl);

    if (rtspUrl.startsWith('rtsp://')) {
      if (!streamUrl || streamUrl === '') {
        enhancedErrorMessage = 'Invalid RTSP URL format. Please check the camera configuration.';
      } else {
        enhancedErrorMessage = `Unable to connect to media server. Please ensure the media server is running and accessible at the configured URL. Stream: ${streamUrl}`;
      }
    }

    updateStatus({
      hasError: true,
      isLoading: false,
      isPlaying: false,
      errorMessage: enhancedErrorMessage,
      connectionStatus: 'disconnected'
    });
    onError?.(enhancedErrorMessage);

    // Auto-retry logic - inline to avoid circular dependency
    if (retryCount < maxRetries) {
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        // Inline retry logic
        if (videoRef.current) {
          updateStatus({
            hasError: false,
            isLoading: true,
            connectionStatus: 'connecting'
          });
          videoRef.current.load();
          if (autoPlay) {
            videoRef.current.play().catch(() => {
              // If retry fails, don't create another retry loop
            });
          }
        }
      }, 5000 * (retryCount + 1)); // Exponential backoff
    }
  }, [updateStatus, onError, rtspUrl, retryCount, maxRetries, autoPlay]);

  const handleRetry = useCallback(() => {
    if (!videoRef.current) return;

    updateStatus({
      hasError: false,
      isLoading: true,
      connectionStatus: 'connecting'
    });

    // Reload the video source
    videoRef.current.load();
    if (autoPlay) {
      videoRef.current.play().catch((error) => {
        console.error('Retry play failed:', error);
        updateStatus({
          hasError: true,
          isLoading: false,
          isPlaying: false,
          errorMessage: 'Retry failed',
          connectionStatus: 'disconnected'
        });
      });
    }
  }, [updateStatus, autoPlay]);

  const toggleMute = () => {
    if (!videoRef.current) return;

    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const toggleFullscreen = () => {
    if (!videoRef.current) return;

    if (!isFullscreen) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // Setup video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadStart = () => updateStatus({ isLoading: true });
    const handleLoadedData = () => updateStatus({ isLoading: false });
    const handleCanPlay = () => {
      updateStatus({ isLoading: false });
      // Use a ref to check autoPlay to avoid dependency on status
      if (autoPlay) {
        // Use setTimeout to avoid calling setState during render
        setTimeout(() => {
          if (video && video.paused) {
            handlePlay();
          }
        }, 0);
      }
    };
    const handleVideoError = () => {
      const error = video.error;
      const errorMessage = error ?
        `Video error: ${error.message} (Code: ${error.code})` :
        'Unknown video error';
      handleError(errorMessage);
    };
    const handlePlaying = () => updateStatus({ isPlaying: true, isLoading: false });
    const handlePaused = () => updateStatus({ isPlaying: false });
    const handleWaiting = () => updateStatus({ isLoading: true });

    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleVideoError);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('pause', handlePaused);
    video.addEventListener('waiting', handleWaiting);

    // Fullscreen change listener
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleVideoError);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('pause', handlePaused);
      video.removeEventListener('waiting', handleWaiting);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [autoPlay]); // Removed status.isPlaying from dependencies

  // Reset retry count when URL changes
  useEffect(() => {
    setRetryCount(0);
    updateStatus({
      hasError: false,
      isLoading: true,
      connectionStatus: 'connecting'
    });
  }, [rtspUrl]);

  // HLS.js setup and management
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const streamUrl = getStreamUrl(rtspUrl);
    if (!streamUrl) {
      handleError('Invalid stream URL');
      return;
    }

    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Check if HLS is supported
    if (Hls.isSupported()) {
      // Create new HLS instance optimized for MediaMTX live streaming
      const hls = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: true,
        // Buffer configuration optimized for 2s segments
        backBufferLength: 20, // Reduced for lower latency
        maxBufferLength: 20,  // Reduced for lower latency
        maxMaxBufferLength: 40,
        liveSyncDurationCount: 2, // Reduced for lower latency
        liveMaxLatencyDurationCount: 4,
        // Gap handling configuration - more tolerant
        maxFragLookUpTolerance: 0.5,
        maxBufferHole: 1.0,
        nudgeOffset: 0.2,
        nudgeMaxRetry: 5,
        // Fragment loading configuration - more aggressive retries
        fragLoadingTimeOut: 15000,
        fragLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 500,
        fragLoadingMaxRetryTimeout: 32000,
        // Manifest loading configuration
        manifestLoadingTimeOut: 8000,
        manifestLoadingMaxRetry: 3,
        manifestLoadingRetryDelay: 500,
        manifestLoadingMaxRetryTimeout: 16000,
        // Level loading configuration
        levelLoadingTimeOut: 8000,
        levelLoadingMaxRetry: 6,
        levelLoadingRetryDelay: 500,
        levelLoadingMaxRetryTimeout: 32000,
      });

      hlsRef.current = hls;

      // Load the stream
      hls.loadSource(streamUrl);
      hls.attachMedia(video);

      // HLS event handlers
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest parsed successfully');
        updateStatus({ isLoading: false, hasError: false, connectionStatus: 'connected' });
        if (autoPlay) {
          video.play().catch((error) => {
            console.error('Autoplay failed:', error);
            updateStatus({ hasError: true, errorMessage: 'Autoplay failed' });
          });
        }
      });

      // Handle media attached
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        console.log('HLS media attached');
        updateStatus({ connectionStatus: 'connecting' });
      });

      // Handle level loaded (quality level)
      hls.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
        console.log('HLS level loaded:', data.level);
        updateStatus({
          quality: data.level === 0 ? 'high' : data.level === 1 ? 'medium' : 'low',
          connectionStatus: 'connected'
        });
      });

      // Handle fragment loaded
      hls.on(Hls.Events.FRAG_LOADED, () => {
        // Reset retry count on successful fragment load
        setRetryCount(0);
        updateStatus({ hasError: false, connectionStatus: 'connected' });
      });

      // Handle buffer events
      hls.on(Hls.Events.BUFFER_APPENDED, () => {
        updateStatus({ isLoading: false });
      });

      hls.on(Hls.Events.BUFFER_EOS, () => {
        console.log('HLS buffer end of stream');
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        console.log('HLS Event:', data.type, data.details, 'Fatal:', data.fatal);

        // Handle non-fatal errors gracefully
        if (!data.fatal) {
          // Common non-fatal errors that should be ignored or handled silently
          const ignorableErrors = [
            'fragGap',
            'fragLoadError',
            'fragParsingError',
            'bufferStalledError',
            'bufferAppendError',
            'bufferAddCodecError',
            'levelLoadError',
            'levelLoadTimeOut'
          ];

          if (ignorableErrors.includes(data.details)) {
            console.log(`Ignoring non-fatal HLS error: ${data.details}`);

            // Try automatic recovery for specific errors
            if (data.details === 'bufferStalledError' || data.details === 'bufferAppendError') {
              setTimeout(() => {
                try {
                  hls.recoverMediaError();
                } catch (e) {
                  console.log('Recovery attempt failed:', e);
                }
              }, 100);
            }
            return;
          }

          // Log other non-fatal errors but continue playback
          console.warn('Non-fatal HLS error (continuing):', data.details);
          return;
        }

        // Handle fatal errors with recovery attempts
        console.error('Fatal HLS Error:', data);

        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.log('Fatal network error - attempting recovery');
            setTimeout(() => {
              try {
                hls.startLoad();
                updateStatus({ hasError: false, connectionStatus: 'connecting' });
              } catch (e) {
                console.error('Network recovery failed:', e);
                handleError(`Network Error: ${data.details || 'Connection failed'}`);
              }
            }, 1000);
            break;

          case Hls.ErrorTypes.MEDIA_ERROR:
            console.log('Fatal media error - attempting recovery');
            setTimeout(() => {
              try {
                hls.recoverMediaError();
                updateStatus({ hasError: false, connectionStatus: 'connecting' });
              } catch (e) {
                console.error('Media recovery failed:', e);
                handleError(`Media Error: ${data.details || 'Playback failed'}`);
              }
            }, 1000);
            break;

          default:
            console.log('Unrecoverable fatal error');
            handleError(`Stream Error: ${data.details || 'Unable to play stream'}`);
            break;
        }
      });

      return () => {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      video.src = streamUrl;
      updateStatus({ isLoading: false, hasError: false });
      if (autoPlay) {
        video.play().catch((error) => {
          console.error('Autoplay failed:', error);
          updateStatus({ hasError: true, errorMessage: 'Autoplay failed' });
        });
      }
    } else {
      handleError('HLS is not supported in this browser');
    }
  }, [rtspUrl, autoPlay, updateStatus, handleError]);

  const getStatusColor = () => {
    if (status.hasError) return 'red';
    if (status.connectionStatus === 'connected' && status.isPlaying) return 'green';
    if (status.connectionStatus === 'connecting' || status.isLoading) return 'yellow';
    return 'gray';
  };

  const getStatusIcon = () => {
    if (status.hasError) return <IconWifiOff size={14} />;
    if (status.connectionStatus === 'connected') return <IconWifi size={14} />;
    if (status.isPlaying) return <IconRecordMail size={14} />;
    return <IconCamera size={14} />;
  };

  return (
    <Card withBorder className={className} style={{ position: 'relative' }}>
      <LoadingOverlay visible={status.isLoading} />

      {/* Header with camera info and status */}
      {showStatus && (
        <Group justify="space-between" mb="sm">
          <Group gap="xs">
            <Text fw={500} size="sm">{cameraName}</Text>
            <Badge
              color={getStatusColor()}
              variant="light"
              leftSection={getStatusIcon()}
              size="sm"
            >
              {status.connectionStatus}
            </Badge>
          </Group>

          {showControls && (
            <Group gap="xs">
              <Tooltip label="Refresh stream">
                <ActionIcon
                  variant="light"
                  size="sm"
                  onClick={handleRetry}
                  disabled={status.isLoading}
                >
                  <IconRefresh size={14} />
                </ActionIcon>
              </Tooltip>

              <Tooltip label="Settings">
                <ActionIcon variant="light" size="sm">
                  <IconSettings size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Group>
      )}

      {/* Video Player */}
      <Box style={{ position: 'relative', width, height }}>
        <video
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#000',
            objectFit: 'contain',
          }}
          muted={isMuted}
          playsInline
          controls={false}
        >
          Your browser does not support HLS video streaming.
        </video>

        {/* Stream Info Message */}
        {rtspUrl && !status.isPlaying && !status.hasError && (
          <Alert
            icon={<IconCamera size={16} />}
            color="blue"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
            }}
          >
            <Stack gap="xs">
              <Text size="sm">Video Stream Ready</Text>
              <Text size="xs" c="dimmed">
                Camera: {cameraName}
              </Text>
              <Text size="xs" c="dimmed">
                Source: {rtspUrl}
              </Text>
              <Text size="xs" c="dimmed">
                Stream: {getStreamUrl(rtspUrl)}
              </Text>
            </Stack>
          </Alert>
        )}

        {/* Error State */}
        {status.hasError && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="red"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
            }}
          >
            <Stack gap="xs">
              <Text size="sm">{status.errorMessage}</Text>
              <Button size="xs" variant="light" onClick={handleRetry}>
                Retry ({retryCount}/{maxRetries})
              </Button>
            </Stack>
          </Alert>
        )}
      </Box>

      {/* Controls */}
      {showControls && (
        <Group justify="space-between" mt="sm">
          <Group gap="xs">
            <ActionIcon
              variant="light"
              onClick={status.isPlaying ? handlePause : handlePlay}
              disabled={status.hasError || status.isLoading}
            >
              {status.isPlaying ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
            </ActionIcon>

            <ActionIcon
              variant="light"
              onClick={handleStop}
              disabled={status.hasError || status.isLoading}
            >
              <IconPlayerStop size={16} />
            </ActionIcon>

            <ActionIcon variant="light" onClick={toggleMute}>
              {isMuted ? <IconVolumeOff size={16} /> : <IconVolume size={16} />}
            </ActionIcon>
          </Group>

          <Group gap="xs">
            <ActionIcon variant="light" onClick={toggleFullscreen}>
              {isFullscreen ? <IconMinimize size={16} /> : <IconMaximize size={16} />}
            </ActionIcon>
          </Group>
        </Group>
      )}

      {/* Connection Quality Indicator */}
      {showStatus && status.connectionStatus === 'connected' && (
        <Progress
          value={status.quality === 'high' ? 100 : status.quality === 'medium' ? 60 : 30}
          color={status.quality === 'high' ? 'green' : status.quality === 'medium' ? 'yellow' : 'red'}
          size="xs"
          mt="xs"
        />
      )}
    </Card>
  );
};

export default RTSPVideoPlayer;
